import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clearStoredMeetings } from '@/features/meetings/storedMeeting';
import { z } from 'zod';
import { demoInfo } from '@/shared/lib/demo';
import { ApiError, request, requestVoid, resetCsrf } from '@/shared/api/client';
import { authMethodsSchema, invitationSchema, loginResultSchema, meSchema, type AuthMethods, type Me } from '@/shared/api/schemas';
import { clearOutbox } from '@/shared/offline/outbox';
import { signWithPasskey } from '@/shared/lib/webauthn';

export const meKey = ['me'] as const;

const emailChangeResultSchema = z.object({ email: z.string() });

/** Текущий пользователь. null — не вошёл (401). */
export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async (): Promise<Me | null> => {
      try {
        return await request('GET', '/api/auth/me', meSchema);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

const PASSWORD_ONLY: AuthMethods = { password: true, google: false, demo: false, phone: false };

/** Какими способами можно войти. Старый сервер такого адреса не знает — тогда только пароль. */
export function useAuthMethods() {
  return useQuery({
    queryKey: ['auth-methods'],
    queryFn: async (): Promise<AuthMethods> => {
      try {
        return await request('GET', '/api/auth/methods', authMethodsSchema);
      } catch {
        return PASSWORD_ONLY;
      }
    },
    staleTime: 5 * 60_000,
  });
}

const ssoDiscoverSchema = z.object({
  sso: z.object({ workspaceId: z.string(), label: z.string(), enforced: z.boolean(), url: z.string() }).nullable(),
});
export type SsoDiscovered = NonNullable<z.infer<typeof ssoDiscoverSchema>['sso']>;

/** Единый вход компании по домену почты: как только набран адрес вида имя@домен.ру. */
export function useSsoDiscover(email: string) {
  const at = email.trim().toLowerCase();
  const domain = /^[^@\s]+@([^@\s]+\.[a-z]{2,})$/.exec(at)?.[1] ?? null;
  return useQuery({
    queryKey: ['sso-discover', domain],
    enabled: domain !== null,
    queryFn: async (): Promise<SsoDiscovered | null> => {
      try {
        return (await request('GET', `/api/auth/sso/discover?email=${encodeURIComponent(at)}`, ssoDiscoverSchema)).sso;
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Вход через провайдера компании: обычно — переход к провайдеру и обратно. В демо провайдер учебный: сервер в памяти
 * сразу впускает человека с этой почтой, страница не уходит.
 */
export function useSsoSignIn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (url: string) => {
      if (demoInfo() === null) {
        leavePage.to(url);
        return;
      }
      await request('GET', url, z.object({ redirect: z.string() }));
      explicitLogout = false;
      resetCsrf();
      await client.invalidateQueries({ queryKey: meKey });
    },
  });
}

/** Подтверждение привязки аккаунта по письму: на странице, кнопкой — сканеры почты сами открывают ссылки из писем. */
export function useSsoConfirm() {
  return useMutation({
    mutationFn: async (v: { readonly workspaceId: string; readonly token: string }) => {
      const r = await request('POST', `/api/auth/sso/${v.workspaceId}/confirm`, z.object({ location: z.string() }), { body: { token: v.token } });
      leavePage.to(r.location);
    },
  });
}

/** Адрес, с которого начинается вход через Google: сервер сам уводит на выбор аккаунта. */
export const GOOGLE_SIGN_IN_URL = '/api/auth/google';

/** Уход со страницы на адрес сервера (вход через Google): отдельно — чтобы в тестах подменить переход. */
export const leavePage = {
  to(url: string): void {
    window.location.assign(url);
  },
};

/**
 * Пользователь сам нажал «Выйти». Тогда следующий вход начинается с главной, а не со страницы,
 * на которой был предыдущий человек.
 */
let explicitLogout = false;
export const isExplicitLogout = () => explicitLogout;

export function useLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) => request('POST', '/api/auth/login', loginResultSchema, { body: input }),
    onSuccess: (result) => {
      if ('user' in result) {
        explicitLogout = false;
        resetCsrf();
        client.setQueryData(meKey, result);
      }
    },
  });
}

const phoneChallengeSchema = z.object({ length: z.number().int(), resendIn: z.number().int(), ttl: z.number().int() });

/** Вход по номеру, шаг 1: SMS с кодом. Ответ одинаков для любого номера. */
export function usePhoneRequest() {
  return useMutation({
    mutationFn: (phone: string) => request('POST', '/api/auth/phone/request', phoneChallengeSchema, { body: { phone } }),
  });
}

/** Вход по номеру, шаг 2: код из SMS. Если у человека есть второй шаг (2FA), придёт twoFactorRequired. */
export function usePhoneVerify() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { phone: string; code: string }) => request('POST', '/api/auth/phone/verify', loginResultSchema, { body: input }),
    onSuccess: (result) => {
      if ('user' in result) {
        explicitLogout = false;
        resetCsrf();
        client.setQueryData(meKey, result);
      }
    },
  });
}

export function useSecondFactor() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => request('POST', '/api/auth/2fa', meSchema, { body: { code } }),
    onSuccess: (me) => {
      explicitLogout = false;
      resetCsrf();
      client.setQueryData(meKey, me);
    },
  });
}

const optionsSchema = z.record(z.string(), z.unknown());

/**
 * Ключ доступа при входе. second=false — вход без пароля (устройство само предложит ключ); true — второй шаг после
 * пароля (подойдёт только ключ этого человека).
 */
export function usePasskeySignIn(second: boolean) {
  const client = useQueryClient();
  const base = second ? '/api/auth/2fa/passkey' : '/api/auth/passkey';
  return useMutation({
    mutationFn: async () => {
      const options = await request('POST', `${base}/options`, optionsSchema);
      const credential = await signWithPasskey(options);
      return request('POST', base, meSchema, { body: { credential } });
    },
    onSuccess: (me) => {
      explicitLogout = false;
      resetCsrf();
      client.setQueryData(meKey, me);
    },
  });
}

/** Адрес возврата в приложение для Mac и iPhone: сервер присылает только его, чужой не принимаем. */
export const APP_TICKET_PREFIX = 'passworkchat://auth?ticket=';

/**
 * Вход ключом доступа для приложения. В окне приложения (WKWebView) ключи не работают, поэтому ключ предъявляют в
 * системном браузере: здесь вход не создаётся, сервер даёт одноразовый билет, а браузер возвращает его приложению.
 */
export function usePasskeyAppSignIn() {
  return useMutation({
    mutationFn: async () => {
      const options = await request('POST', '/api/auth/passkey/options', optionsSchema);
      const credential = await signWithPasskey(options);
      const { redirect } = await request('POST', '/api/auth/passkey', z.object({ redirect: z.string() }), { body: { credential, app: true } });
      if (!redirect.startsWith(APP_TICKET_PREFIX)) throw new Error('Неверный адрес возврата');
      return redirect;
    },
    onSuccess: (redirect) => {
      leavePage.to(redirect);
    },
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    onMutate: async () => {
      explicitLogout = true;
      // Запросы, начатые до выхода, могли бы ответить уже после него и «вернуть» пользователя.
      await client.cancelQueries();
    },
    mutationFn: async () => {
      // Файрвол отвечает редиректом/пустым ответом — нам важен только факт выхода.
      await requestVoid('POST', '/api/auth/logout').catch(() => undefined);
    },
    onSettled: async () => {
      resetCsrf();
      clearStoredMeetings();
      await client.cancelQueries();
      client.setQueryData(meKey, null);
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== meKey[0] });
      // Явный выход: неотправленное с этого устройства тоже стирается.
      await clearOutbox();
    },
  });
}

export function useRequestReset() {
  return useMutation({ mutationFn: (email: string) => requestVoid('POST', '/api/auth/password-reset/request', { body: { email } }) });
}

export function useConfirmReset() {
  return useMutation({
    mutationFn: (input: { token: string; password: string }) => requestVoid('POST', '/api/auth/password-reset/confirm', { body: input }),
  });
}

/** Ссылка из письма о смене e-mail: адрес меняется, вошедший остаётся внутри с новым адресом. */
export function useConfirmEmailChange() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => request('POST', '/api/auth/email-change/confirm', emailChangeResultSchema, { body: { token } }),
    onSuccess: () => client.invalidateQueries({ queryKey: meKey }),
  });
}

/** Приглашение по ссылке из письма. Недействительное — ошибка 404 (без повторов). */
export function useInvitation(token: string) {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: () => request('GET', `/api/auth/invites/${token}`, invitationSchema),
    retry: false,
    staleTime: Infinity,
  });
}

/** Принять приглашение: имя и пароль — и человек сразу внутри, без отдельного входа. */
export function useAcceptInvite(token: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; password: string; language: 'ru' | 'en' }) => request('POST', `/api/auth/invites/${token}`, meSchema, { body: input }),
    onSuccess: (me) => {
      explicitLogout = false;
      resetCsrf();
      client.setQueryData(meKey, me);
    },
  });
}
